import { Injectable, inject, signal } from '@angular/core';
import { FuseConfigService } from '@library/services/config';
import { LOCAL_STORAGE_KEYS } from 'app/models/localStorageKeys.enum';
import { SecurityProfileService } from 'app/services/securityProfile.service';
import { StorageService } from 'app/services/storage.service';
import { ROLES } from 'shared-models';

export type ThemeScheme = 'light' | 'dark';

const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Elección de tema claro u oscuro.
 *
 * No pinta nada por su cuenta: escribe `scheme` en la configuración de Fuse y es
 * `LayoutComponent` quien pone la clase en el `<body>`. Aquí solo vive la decisión y su
 * persistencia.
 *
 * **Solo un superadmin elige.** Para el resto el esquema se queda en `'auto'`, que es como
 * arranca la app: lo decide el sistema operativo. Y no basta con esconder el botón — el
 * valor guardado también se ignora, porque si no bastaría con que otra persona hubiera
 * usado antes ese navegador para heredar su tema.
 */
@Injectable({ providedIn: 'root' })
export class ThemeSchemeService {
  private readonly config = inject(FuseConfigService);
  private readonly storage = inject(StorageService);
  private readonly securityProfile = inject(SecurityProfileService);

  /** Lo que se está viendo, con `'auto'` ya resuelto contra el sistema. */
  readonly isDark = signal(false);

  constructor() {
    this.applyForCurrentUser();
  }

  canChoose(): boolean {
    return this.securityProfile.getRole() === ROLES.SUPERADMIN;
  }

  /**
   * Deja el esquema en lo que le toca a quien está dentro. Se llama al arrancar —donde el
   * rol ya se puede leer del token guardado— y otra vez desde el botón de la cabecera,
   * que es lo que se crea justo después de iniciar sesión. Sin esa segunda pasada, entrar
   * con otra cuenta en la misma pestaña heredaría el tema de la anterior: iniciar sesión
   * no recarga la aplicación.
   *
   * ⚠️ **Cambiar de empresa dentro de la sesión no vuelve a pasar por aquí**: la cabecera
   * no se recrea. Si se salta a una empresa donde no se es superadmin, el tema se queda
   * como estaba hasta la siguiente recarga. Se ha dejado así a propósito: engancharlo al
   * cambio de empresa obliga a depender del orden en que se actualizan la empresa
   * seleccionada y el rol, y leer el rol viejo daría el fallo contrario y peor —aplicar
   * el tema de un superadmin a quien no lo es—. Al recargar se corrige solo.
   */
  applyForCurrentUser(): void {
    const saved = this.storage.getItem(LOCAL_STORAGE_KEYS.THEME_SCHEME);
    const chosen: ThemeScheme | null = saved === 'light' || saved === 'dark' ? saved : null;
    const scheme: ThemeScheme | 'auto' = chosen && this.canChoose() ? chosen : 'auto';

    this.config.config = { scheme };
    this.isDark.set(this.resolve(scheme));
  }

  toggle(): void {
    this.set(this.isDark() ? 'light' : 'dark');
  }

  set(scheme: ThemeScheme): void {
    if (!this.canChoose()) return;

    this.config.config = { scheme };
    this.storage.setItem(LOCAL_STORAGE_KEYS.THEME_SCHEME, scheme);
    this.isDark.set(scheme === 'dark');
  }

  /**
   * `'auto'` no dice qué se ve, solo que lo decide el sistema. Sin resolverlo, el botón
   * enseñaría el icono equivocado en cuanto alguien tenga el sistema en claro.
   */
  private resolve(scheme: ThemeScheme | 'auto'): boolean {
    if (scheme !== 'auto') return scheme === 'dark';
    return typeof window !== 'undefined' && window.matchMedia(DARK_QUERY).matches;
  }
}
